/**
 * Full-graph validator — the single quality gate for AI-built/edited graphs.
 *
 * Two tiers (spec: "Validator severity tiers"):
 *   errors   — renderable tier: malformed graphs that must block an AI commit
 *   warnings — backtestable tier: legitimate-while-editing gaps (missing entry
 *              rules, unconnected inputs). Surfaced as advice, never blocking.
 *
 * Type-compatibility looseness: number and boolean are mutually compatible.
 * Real strategies wire boolean signals (crossover.signal) into inputs declared
 * ['number','ohlcv'] (entryRules.signal) — schema acceptedTypes are stricter
 * than actual editor behavior, and erroring there would flag every real graph.
 */
import { isTradePlacerType } from '@/features/backtest/schemas/compound'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { validateNodeConnection } from '@/features/backtest/utils/universal-node-validation'
import type {
  UniversalNodeSchema,
  ParameterDefinition,
} from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import type { EngineError, MaterializedInput } from './types'
import { isInputRequiredNow } from '@/features/backtest/schemas/input-activity'

export interface ValidationReport {
  errors: EngineError[]
  warnings: EngineError[]
}

const STRATEGY_CATEGORY = 'entry-exit-logic'

export function validateGraph(nodes: GraphNode[], edges: GraphEdge[]): ValidationReport {
  const errors: EngineError[] = []
  const warnings: EngineError[] = []

  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const schemaById = new Map<string, UniversalNodeSchema | undefined>()
  for (const node of nodes) {
    const schema = UniversalNodeRegistry.getSchemaResolved(node.type)
    schemaById.set(node.id, schema)
    if (!schema) {
      errors.push({
        code: 'UNKNOWN_TYPE',
        stepId: node.id,
        message: `Unknown node type "${node.type}"`,
      })
    }
  }

  validateParams(nodes, schemaById, errors)
  validateEdges(edges, nodeById, schemaById, errors)
  validateCycles(nodes, edges, errors)
  validatePrimaryPriceData(nodes, errors)
  validateRequiredInputs(nodes, edges, schemaById, warnings)
  validateStrategyCompleteness(nodes, edges, schemaById, warnings)

  return { errors, warnings }
}

// ── Parameters ──────────────────────────────────────────────

function paramsToRecord(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw)) {
    const out: Record<string, unknown> = {}
    for (const p of raw) {
      if (p && typeof p === 'object' && 'name' in p) {
        out[(p as { name: string }).name] = (p as { value?: unknown }).value
      }
    }
    return out
  }
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

function validateParamValue(
  nodeId: string,
  def: ParameterDefinition,
  value: unknown,
  errors: EngineError[],
): void {
  if (value === undefined || value === null) return

  if (def.type === 'int' || def.type === 'float') {
    const num = typeof value === 'number' ? value : Number(value)
    if (Number.isNaN(num)) return // non-numeric strings: tolerated on live graphs
    if ((def.min !== undefined && num < def.min) || (def.max !== undefined && num > def.max)) {
      errors.push({
        code: 'PARAM_OUT_OF_RANGE',
        stepId: nodeId,
        message: `"${def.name}" = ${num} out of range`,
        suggestion: `Allowed: ${def.min ?? '-∞'}..${def.max ?? '∞'}`,
      })
    }
    return
  }

  if (def.type === 'select') {
    const allowed = def.options.map((o) => o.value)
    if (!allowed.some((v) => v === value || String(v) === String(value))) {
      errors.push({
        code: 'PARAM_NOT_IN_ENUM',
        stepId: nodeId,
        message: `"${def.name}" = "${String(value)}" is not a valid option`,
        suggestion: `Allowed: ${allowed.map(String).join(', ')}`,
      })
    }
  }
}

function validateParams(
  nodes: GraphNode[],
  schemaById: Map<string, UniversalNodeSchema | undefined>,
  errors: EngineError[],
): void {
  for (const node of nodes) {
    const schema = schemaById.get(node.id)
    if (!schema) continue
    const record = paramsToRecord(node.data?.parameters)
    for (const def of schema.parameters) {
      validateParamValue(node.id, def, record[def.name], errors)
    }
    // Unknown param KEYS are deliberately tolerated here: live graphs carry
    // legacy/preset params. Recipe-level unknown params are caught earlier by
    // materializeNode (PARAM_UNKNOWN).
  }
}

// ── Edges: handles + compatibility ─────────────────────────

function validateEdges(
  edges: GraphEdge[],
  nodeById: Map<string, GraphNode>,
  schemaById: Map<string, UniversalNodeSchema | undefined>,
  errors: EngineError[],
): void {
  for (const edge of edges) {
    const source = nodeById.get(edge.source)
    const target = nodeById.get(edge.target)
    if (!source || !target) {
      errors.push({
        code: 'UNKNOWN_STEP',
        message: `Edge "${edge.id}" references a missing node`,
      })
      continue
    }
    const sourceSchema = schemaById.get(edge.source)
    const targetSchema = schemaById.get(edge.target)
    if (!sourceSchema || !targetSchema) continue // UNKNOWN_TYPE already reported

    // Source handle must exist
    const output = edge.sourceHandle
      ? sourceSchema.outputs.find((o) => o.id === edge.sourceHandle)
      : sourceSchema.outputs[0]
    if (!output) {
      errors.push({
        code: 'UNKNOWN_HANDLE',
        stepId: edge.source,
        handle: edge.sourceHandle ?? undefined,
        message: `"${source.type}" has no output "${edge.sourceHandle}"`,
        suggestion: `Available: ${sourceSchema.outputs.map((o) => o.id).join(', ')}`,
      })
      continue
    }

    // Target handle: schema input, or dynamic handle on multi-input nodes
    const inputDef = edge.targetHandle
      ? targetSchema.inputs.find((i) => i.id === edge.targetHandle)
      : targetSchema.inputs[0]
    const isDynamicMulti = targetSchema.inputHandler?.type === 'multi'
    if (!inputDef && !isDynamicMulti) {
      errors.push({
        code: 'UNKNOWN_HANDLE',
        stepId: edge.target,
        handle: edge.targetHandle ?? undefined,
        message: `"${target.type}" has no input "${edge.targetHandle}"`,
        suggestion: `Available: ${targetSchema.inputs.map((i) => i.id).join(', ') || '(dynamic)'}`,
      })
      continue
    }

    // The canvas's own rule (universal-node-validation.ts): type, source
    // category, node-type filter, and the Target / Entry Rules modes. A wire
    // the user cannot draw is one the AI cannot build (audit 2026-10-06).
    // Dynamic And/Or handles are checked too.
    const verdict = validateNodeConnection(
      sourceSchema.type,
      targetSchema.type,
      edge.targetHandle ?? inputDef?.id,
      output.id,
      undefined,
      target.data as Record<string, unknown> | undefined,
    )
    if (!verdict.isValid) {
      errors.push({
        code: 'INCOMPATIBLE_CONNECTION',
        stepId: edge.target,
        handle: inputDef?.id ?? edge.targetHandle ?? undefined,
        message: `"${source.type}.${output.id}" cannot feed "${target.type}.${edge.targetHandle ?? inputDef?.id}": ${verdict.errorMessage ?? 'not allowed'}`,
        ...(inputDef
          ? {
              suggestion: `Input accepts: ${inputDef.acceptedTypes.join(', ')}${
                inputDef.acceptedSources.includes('*')
                  ? ''
                  : ` from ${inputDef.acceptedSources.join(', ')}`
              }`,
            }
          : {}),
      })
    }
  }
}

// ── Cycles ──────────────────────────────────────────────────

function validateCycles(nodes: GraphNode[], edges: GraphEdge[], errors: EngineError[]): void {
  const inDegree = new Map<string, number>(nodes.map((n) => [n.id, 0]))
  const downstream = new Map<string, string[]>()
  for (const edge of edges) {
    if (!inDegree.has(edge.source) || !inDegree.has(edge.target)) continue
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1)
    const list = downstream.get(edge.source) ?? []
    list.push(edge.target)
    downstream.set(edge.source, list)
  }
  const queue = nodes.filter((n) => (inDegree.get(n.id) ?? 0) === 0).map((n) => n.id)
  let visited = 0
  while (queue.length > 0) {
    const id = queue.shift()!
    visited++
    for (const t of downstream.get(id) ?? []) {
      const d = (inDegree.get(t) ?? 1) - 1
      inDegree.set(t, d)
      if (d === 0) queue.push(t)
    }
  }
  if (visited < nodes.length) {
    const cyclic = nodes.filter((n) => (inDegree.get(n.id) ?? 0) > 0).map((n) => n.id)
    errors.push({
      code: 'CYCLE',
      message: `Graph contains a cycle involving: ${cyclic.join(', ')}`,
      suggestion: 'Remove one of the edges in the loop',
    })
  }
}

// ── Primary price data ──────────────────────────────────────

function validatePrimaryPriceData(nodes: GraphNode[], errors: EngineError[]): void {
  const primaries = nodes.filter((n) => n.type === 'priceData' && n.data?.isPrimary === true)
  if (primaries.length > 1) {
    errors.push({
      code: 'MULTIPLE_PRIMARY_PRICE_DATA',
      message: `${primaries.length} priceData nodes are marked primary (${primaries.map((n) => n.id).join(', ')})`,
      suggestion: 'Exactly one priceData node drives the run',
    })
  }
}

// ── Required inputs (warnings) ──────────────────────────────

function validateRequiredInputs(
  nodes: GraphNode[],
  edges: GraphEdge[],
  schemaById: Map<string, UniversalNodeSchema | undefined>,
  warnings: EngineError[],
): void {
  const connectedHandles = new Map<string, Set<string>>()
  for (const edge of edges) {
    const set = connectedHandles.get(edge.target) ?? new Set()
    if (edge.targetHandle) set.add(edge.targetHandle)
    connectedHandles.set(edge.target, set)
  }

  for (const node of nodes) {
    const schema = schemaById.get(node.id)
    if (!schema?.inputHandler) continue
    // Debug nodes (Debug Values / Chart) are diagnostic sinks, not strategy
    // logic — a dangling input there is not a run-readiness gap. (They are also
    // excluded from UNREACHABLE_SUBGRAPH in validateStrategyCompleteness.)
    if (schema.category === 'debug') continue

    const connected = connectedHandles.get(node.id) ?? new Set()
    const staticHandles = new Set(
      (Array.isArray(node.data?.inputs) ? (node.data.inputs as MaterializedInput[]) : [])
        .filter((i) => i?.inputType === 'static')
        .map((i) => i.name),
    )
    // A discriminated-union input (e.g. condition.input_2) is toggled static via
    // its discriminator PARAMETER (`inputBType`). The editor only writes an
    // `inputType: 'static'` marker to data.inputs when a prior entry existed, so
    // the parameter is the reliable signal — read it from the schema.
    const params = paramsToRecord(node.data?.parameters)
    for (const inputDef of schema.inputs) {
      const du = inputDef.discriminatedUnion
      if (du && params[du.parameterName] === 'static') staticHandles.add(inputDef.id)
    }
    const satisfied = (handle: string) => connected.has(handle) || staticHandles.has(handle)

    // Settings-dependent requiredness (`activeWhen`, `requiredWhen`,
    // `optional`), the same rule the node face and the pre-run check read
    // (schemas/input-activity.ts). E.g. target.signal may dangle for
    // Ticks/%-Trade, which compute off the entry price, but not for Static
    // Value / Condition targets.
    const isRequired = (inputDef: (typeof schema.inputs)[number]): boolean =>
      isInputRequiredNow(schema, inputDef, params)

    const { type, minInputs = 2 } = schema.inputHandler
    if (type === 'single' || type === 'dual' || type === 'all') {
      const count = type === 'single' ? 1 : type === 'dual' ? 2 : schema.inputs.length
      for (const inputDef of schema.inputs.slice(0, count)) {
        if (!isRequired(inputDef)) continue
        if (!satisfied(inputDef.id)) {
          warnings.push({
            code: 'UNCONNECTED_REQUIRED_INPUT',
            stepId: node.id,
            handle: inputDef.id,
            message: `"${node.type}" input "${inputDef.id}" is not connected`,
          })
        }
      }
    } else if (type === 'multi') {
      const count = connected.size + staticHandles.size
      if (count < minInputs) {
        warnings.push({
          code: 'UNCONNECTED_REQUIRED_INPUT',
          stepId: node.id,
          message: `"${node.type}" needs at least ${minInputs} inputs, has ${count}`,
        })
      }
    }
  }
}

// ── Strategy completeness (warnings) ────────────────────────

function validateStrategyCompleteness(
  nodes: GraphNode[],
  edges: GraphEdge[],
  schemaById: Map<string, UniversalNodeSchema | undefined>,
  warnings: EngineError[],
): void {
  const strategyNodes = nodes.filter((n) => schemaById.get(n.id)?.category === STRATEGY_CATEGORY)

  // Trade-placing roots: entryRules, plus the self-contained trade nodes.
  // simpleTrade takes a boolean signal directly and trade (Advanced Trade)
  // is itself the placer (its required entry input enforces entryRules
  // separately) — neither needs a standalone Entry Rules node.
  // The placers are declared on their schemas (schemas/compound.ts); on a graph
  // not yet lowered (the AI's) that includes the Trade Manager.
  if (!nodes.some((n) => isTradePlacerType(n.type))) {
    warnings.push({
      code: 'MISSING_ENTRY_RULES',
      message: 'No trade node — the strategy cannot place trades',
      suggestion: 'Add a tradeManager and wire the entry signal into its trigger',
    })
  }

  if (strategyNodes.length === 0) return

  // Reachability: a node "reaches" the strategy layer if a directed path
  // leads from it to any strategy-category node.
  const downstream = new Map<string, string[]>()
  for (const edge of edges) {
    const list = downstream.get(edge.source) ?? []
    list.push(edge.target)
    downstream.set(edge.source, list)
  }

  const reaches = new Map<string, boolean>()
  const visit = (id: string, stack: Set<string>): boolean => {
    if (reaches.has(id)) return reaches.get(id)!
    if (schemaById.get(id)?.category === STRATEGY_CATEGORY) {
      reaches.set(id, true)
      return true
    }
    if (stack.has(id)) return false // cycle guard
    stack.add(id)
    const ok = (downstream.get(id) ?? []).some((t) => visit(t, stack))
    stack.delete(id)
    reaches.set(id, ok)
    return ok
  }

  for (const node of nodes) {
    const schema = schemaById.get(node.id)
    if (!schema) continue
    if (schema.category === STRATEGY_CATEGORY || schema.category === 'debug') continue
    if (!visit(node.id, new Set())) {
      warnings.push({
        code: 'UNREACHABLE_SUBGRAPH',
        stepId: node.id,
        message: `"${node.type}" (${node.id}) does not feed into the strategy`,
      })
    }
  }
}
